import {
  registerWebMcpTool,
  type WebMcpTool,
} from '@ibercar/agent-compat';
import { useEffect } from 'react';

const ADMIN_AGENT_SECTIONS = {
  dashboard: { path: '/dashboard', title: 'Dashboard' },
  reserves: { path: '/reserves', title: 'Reservas activas' },
  accounting: { path: '/accounting', title: 'Contabilidad' },
  compliance: { path: '/compliance', title: 'Cumplimiento' },
  vtc: { path: '/vtc', title: 'Configuración VTC' },
} as const;

type AdminAgentSection = keyof typeof ADMIN_AGENT_SECTIONS;
type OpenAdminSectionInput = {
  section: AdminAgentSection;
};

interface UseAdminAgentNavigationToolOptions {
  enabled: boolean;
  navigate: (path: string) => void;
  onNavigate?: () => void;
}

/**
 * Expone la capacidad de navegación que existe hoy. El scope y la importancia
 * orientan la decisión de delegación; no representan un techo normativo para
 * futuras capacidades que el propio Admin autorice.
 */
export function useAdminAgentNavigationTool({
  enabled,
  navigate,
  onNavigate,
}: UseAdminAgentNavigationToolOptions): void {
  useEffect(() => {
    if (!enabled) return undefined;

    const tool: WebMcpTool<OpenAdminSectionInput> = {
      name: 'open_admin_section',
      title: 'Abrir sección de Admin',
      description:
        'Abre una sección operativa visible del panel de administración Ibercar.',
      delegation: {
        permissionScope: 'admin.navigation.open',
        importance: 'routine',
        reason: 'Cambia la sección visible sin modificar datos.',
      },
      inputSchema: {
        type: 'object',
        additionalProperties: false,
        properties: {
          section: {
            type: 'string',
            description: 'Sección del panel que se debe abrir.',
            enum: ['dashboard', 'reserves', 'accounting', 'compliance', 'vtc'],
          },
        },
        required: ['section'],
      },
      annotations: {
        readOnlyHint: true,
        untrustedContentHint: false,
      },
      execute: ({ section }) => {
        const destination = ADMIN_AGENT_SECTIONS[section];
        if (!destination) {
          throw new Error(
            'Sección no válida. Usa dashboard, reserves, accounting, compliance o vtc.',
          );
        }
        navigate(destination.path);
        onNavigate?.();
        return {
          ok: true,
          section,
          path: destination.path,
          title: destination.title,
        };
      },
    };

    const registration = registerWebMcpTool(tool);
    return registration.unregister;
  }, [enabled, navigate, onNavigate]);
}
